import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, Flex } from '@opendoor/bricks/core';
import { novo } from '@opendoor/bricks/theme';
import { useObservability } from '@opendoor/observability';

import HelperText from './HelperText';
import { RadioLabel } from './RadioInput';

const CheckedCheckbox: React.FC = () => (
  <>
    <svg
      className="empty"
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.75"
        y="0.75"
        width="28.5"
        height="28.5"
        rx="5.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
    <svg
      className="checked"
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="30" height="30" rx="6" fill={`${novo.colors.warmgrey950}`} />
      <path
        d="M13.539 21.2356C13.9608 21.2356 14.2855 21.0626 14.513 20.7166L21.1674 10.4221C21.248 10.2989 21.3072 10.1804 21.3451 10.0667C21.383 9.9529 21.402 9.83915 21.402 9.7254C21.402 9.44576 21.3096 9.21589 21.1247 9.03578C20.9399 8.85568 20.7029 8.76562 20.4138 8.76562C20.2147 8.76562 20.0488 8.80591 19.9161 8.88649C19.7834 8.96232 19.6554 9.09503 19.5322 9.28461L13.5105 18.8255L10.425 14.9153C10.2023 14.6357 9.92737 14.4958 9.60033 14.4958C9.31121 14.4958 9.07186 14.5883 8.88228 14.7731C8.69269 14.9579 8.5979 15.1926 8.5979 15.4769C8.5979 15.6049 8.61923 15.7281 8.66189 15.8466C8.70928 15.9651 8.78512 16.086 8.88939 16.2092L12.5721 20.745C12.8375 21.0721 13.1598 21.2356 13.539 21.2356Z"
        fill="white"
      />
    </svg>
  </>
);

type CheckboxContainerProps = {
  error?: boolean;
};
const CheckboxContainer = styled.div<CheckboxContainerProps>`
  border-radius: ${novo.radii.roundedSmooth};
  border: 1px solid ${novo.colors.warmgrey300};

  &:hover {
    border: 1px solid ${novo.colors.warmgrey800};
    outline: 1px solid ${novo.colors.warmgrey800};

    & .empty {
      color: ${novo.colors.warmgrey800};
    }
  }

  & .checked {
    display: none;
  }
  & .empty {
    color: ${novo.colors.warmgrey300};
  }

  input[type='checkbox']:checked + & {
    border: 1px solid ${novo.colors.warmgrey950};
    outline: 1px solid ${novo.colors.warmgrey950};
  }

  input[type='checkbox']:checked + & {
    .checked {
      display: block;
    }
    .empty {
      display: none;
    }
  }

  input[type='checkbox']:disabled + & {
    border: 1px solid ${novo.colors.warmgrey300};
    color: ${novo.colors.warmgrey700};

    & .empty {
      color: ${novo.colors.warmgrey300};
    }
  }

  input[type='checkbox']:focus-within + & {
    border: 1px solid ${novo.colors.brandBlue600};
    outline: 2px solid ${novo.colors.brandBlue600};
    & .empty {
      color: ${novo.colors.warmgrey800};
    }
  }

  ${({ error }) =>
    error
      ? css`
          border: 1px solid ${novo.colors.error500};
          outline: 1px solid ${novo.colors.error500};
          color: ${novo.colors.error500};

          & .empty {
            color: ${novo.colors.error500};
          }
        `
      : ''};
`;

const Input = styled.input`
  opacity: 0;
  position: absolute;
`;

export type CheckboxInputProps = {
  id: string;
  children: React.ReactNode;
  additionalChildren?: React.ReactNode;
  analyticsName?: string;
  errorText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  showCheckbox?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const CheckboxInput: React.FC<CheckboxInputProps> = ({
  id,
  disabled,
  children,
  additionalChildren,
  analyticsName,
  error,
  errorText,
  showCheckbox = true,
  ...inputProps
}) => {
  const { trackJourneyEvent } = useObservability();

  const onClick = (e: React.MouseEvent<HTMLInputElement>) => {
    inputProps.onClick && inputProps.onClick(e);
    if (analyticsName) {
      const data = { targetName: analyticsName, optionName: inputProps.value };
      trackJourneyEvent('platform', 'checkboxClickEvent', data);
    }
  };
  return (
    <div>
      <Input type="checkbox" id={id} {...inputProps} disabled={disabled} onClick={onClick} />
      <CheckboxContainer error={error}>
        <RadioLabel htmlFor={id}>
          <Flex flexDirection="row" alignItems="center" gap="22px">
            <div style={{ flex: 1 }}>{children}</div>
            {showCheckbox ? <CheckedCheckbox /> : null}
          </Flex>
        </RadioLabel>
        {additionalChildren ? (
          <RadioLabel as="div" style={{ paddingTop: '0' }}>
            {additionalChildren}
          </RadioLabel>
        ) : null}
      </CheckboxContainer>
      {error && errorText && (
        <Box mt={['10px', null, '12px']}>
          <HelperText error>{errorText}</HelperText>
        </Box>
      )}
    </div>
  );
};

type CheckboxGroupProps = {
  id: string;
  children: React.ReactNode;
  ariaLabelledBy?: string;
};

const CheckboxGroupContainer: React.FC<CheckboxGroupProps> = ({ id, children, ariaLabelledBy }) => {
  return (
    <Flex flexDirection="column" gap="16px" id={id} role="group" aria-labelledby={ariaLabelledBy}>
      {children}
    </Flex>
  );
};

export type CheckboxGroupInputProps = {
  id: string;
  error?: boolean;
  name: string;
  analyticsName?: string;
  options: Array<CheckboxInputProps>;
  labelId?: string;
};

export const CheckboxGroupInput: React.FC<CheckboxGroupInputProps> = ({
  id,
  options,
  labelId,
  analyticsName,
}) => {
  return (
    <CheckboxGroupContainer id={id} ariaLabelledBy={labelId}>
      {options.map((props) => {
        return <CheckboxInput key={props.id} {...props} analyticsName={analyticsName} />;
      })}
    </CheckboxGroupContainer>
  );
};
